import type {
  ConstellationEdge,
  ConstellationNode,
  ConstellationView,
  ElementWorkCounts,
} from './types';
import type { RelationDirection } from './types';

/**
 * Zamiana odpowiedzi API na **płaską listę wierszy** do wyrenderowania.
 *
 * Płaska lista, a nie zagnieżdżone elementy, jest tu standardem implementacji,
 * nie wyborem: wzorzec `tree` z WAI-ARIA wymaga jednego pojemnika `role="tree"`
 * i rodzeństwa `role="treeitem"` z jawnym `aria-level`, a nawigacja klawiaturą
 * potrzebuje kolejności wierszy jako jednego wymiaru.
 *
 * Cała logika układu siedzi tutaj — czysto, bez Reacta — bo to tu mieszkają
 * decyzje produktowe (kolejność sekcji, próg zwijania, obcięcie cyklu), a nie w JSX.
 */

interface RowBase {
  key: string;
  elementId: number;
  title: string;
  type: string | null;
  depth: number;
}

/** Wiersz elementu — po `has` (szkielet) albo po zależności (adnotacja). */
export interface ElementRow extends RowBase {
  kind: 'element';
  direction: RelationDirection;
  /**
   * Ile jeszcze da się rozwinąć; `null` = jeszcze nie wiemy (nie pytaliśmy).
   *
   * ⚠ Liczy się z `childCount + dependsOnCount + usedByCount` węzła, mimo że dwóch ostatnich
   * wiersz już NIE POKAZUJE (decyzja 166). To jedyne miejsce, w którym są jeszcze potrzebne
   * na froncie: element bez dzieci po `has`, ale z zależnościami, ma dostać trójkąt.
   */
  expandableCount: number | null;
  /**
   * Element jest już na ścieżce od korzenia — znacznik `↺` (decyzja 25).
   * Rozwijanie zatrzymuje się tutaj, bo `depends-on` ma cykle z definicji.
   */
  repeated: boolean;
}

/** „⋯ jeszcze N" — reszta rodzeństwa ponad progiem (decyzja 42). */
export interface MoreRow {
  kind: 'more';
  key: string;
  anchorId: number;
  section: RelationDirection;
  depth: number;
  hiddenCount: number;
}

export type TreeRow = ElementRow | MoreRow;

/**
 * Dane drzewka: odpowiedź startowa **plus** wszystko, co doszło z rozwijania.
 *
 * Widoki scalamy zamiast zagnieżdżać komponenty, bo układ musi zostać jedną
 * płaską listą — inaczej nawigacja klawiaturą i `aria-level` rozjeżdżają się
 * z tym, co widać.
 */
export function mergeViews(views: readonly ConstellationView[]): ConstellationData {
  const nodes = new Map<number, ConstellationNode>();
  const children = new Map<number, ConstellationNode[]>();
  const edges = new Map<number, ConstellationEdge[]>();
  const work = new Map<number, ElementWorkCounts>();
  const seenEdges = new Set<string>();

  for (const view of views) {
    /*
     * Liczby pracy jadą RAZEM z kształtem (decyzja 173).
     *
     * ⭐ Tu nadpisujemy ŚWIADOMIE, odwrotnie niż przy węzłach: kształt tego samego elementu
     *   jest w każdej odpowiedzi ten sam, ale liczby są migawką z chwili odpowiedzi — więc
     *   z dwóch wersji prawdziwsza jest ta, która przyszła później.
     * ⛔ Brak wpisu znaczy „nie policzono", nie „zero" (decyzja 167): serwer oddaje wiersz dla
     *   każdego elementu odpowiedzi, więc pusta lista to wyłącznie pad albo budżet portu.
     */
    for (const counts of view.work) {
      work.set(counts.elementId, counts);
    }

    for (const node of view.spine) {
      // Pierwszy wygrywa: korzeń podwidoku to ten sam element co wiersz,
      // pod którym kliknięto, więc nadpisywanie tylko podmieniałoby go na siebie.
      if (!nodes.has(node.elementId)) {
        nodes.set(node.elementId, node);
      }

      if (node.parentId === null) {
        continue;
      }

      const existing = children.get(node.parentId);
      if (existing) {
        if (!existing.some((sibling) => sibling.elementId === node.elementId)) {
          existing.push(node);
        }
      } else {
        children.set(node.parentId, [node]);
      }
    }

    for (const edge of view.annotations) {
      // Ta sama krawędź przychodzi ponownie przy każdym rozwinięciu sąsiada —
      // bez odsiania wiersz zdublowałby się na ekranie.
      const fingerprint = `${edge.anchorElementId}:${edge.relationId}`;
      if (seenEdges.has(fingerprint)) {
        continue;
      }
      seenEdges.add(fingerprint);

      const existing = edges.get(edge.anchorElementId);
      if (existing) {
        existing.push(edge);
      } else {
        edges.set(edge.anchorElementId, [edge]);
      }
    }
  }

  return {
    rootElementId: views[0]?.rootElementId ?? 0,
    nodes,
    children,
    edges,
    work,
    // Rozwinięcie kliknięciem nie unieważnia obcięcia z pierwszego malowania.
    truncated: views.some((view) => view.spineTruncated),
  };
}

export interface ConstellationData {
  rootElementId: number;
  nodes: ReadonlyMap<number, ConstellationNode>;
  children: ReadonlyMap<number, ConstellationNode[]>;
  edges: ReadonlyMap<number, ConstellationEdge[]>;
  /** Liczby pracy przy wierszu; **brak klucza = „nie policzono"**, nie „zero" (decyzja 173). */
  work: ReadonlyMap<number, ElementWorkCounts>;
  truncated: boolean;
}

export interface BuildOptions {
  /**
   * Rozwinięte węzły. Korzeń jest rozwinięty zawsze — drzewko zwinięte
   * do jednego wiersza nie jest widokiem, tylko zagadką.
   */
  expanded: ReadonlySet<number>;

  /**
   * Ile pozycji sekcji pokazać, zanim reszta schowa się pod „⋯ jeszcze N"
   * (decyzja 42, próg ustawialny 5/7/10/wszystko). `null` = bez progu.
   *
   * ⚠ Próg działa **per sekcja**, nie łącznie na węzeł. Inaczej element
   * z dwudziestoma podelementami zjadłby cały limit i sekcje zależności
   * zniknęłyby **bez śladu** — a od decyzji 166 wiersz nie niesie już licznika
   * `↑N`, więc rozwinięta sekcja `used-by` jest JEDYNYM miejscem, w którym widać,
   * że coś jest szeroko używane. Argument z decyzji 47 nie zniknął — przeniósł się
   * z liczby na sekcję, przez co ten próg stał się ważniejszy, nie mniej ważny.
   */
  siblingCap: number | null;

  /** Sekcje ukryte soczewką. Soczewka to FILTR, nie osobny kręgosłup (decyzja 51). */
  hiddenSections?: ReadonlySet<RelationDirection>;

  /**
   * Ciepło do WYBORU widocznej siódemki (decyzja 45, która domyka otwarte pytanie
   * z decyzji 42: „które pozycje trafiają do widocznej siódemki").
   *
   * ⚠ Ciepło **wybiera**, ale **nie sortuje tego, co widać**. Kolejność wyświetlania
   * zostaje alfabetyczna — inaczej odtworzylibyśmy problem nazwany w samej decyzji 45:
   * *„pozycje skaczą, gdy ktoś inny domknie zadanie — pamięć mięśniowa przestaje
   * działać"*. Doprecyzowanie „kreska «spokojne»" jest tam nadal otwarte, więc go
   * nie wymyślam.
   */
  heat?: ReadonlyMap<number, { draugrCount: number; activeCount: number }>;

  /**
   * Sekcje, w których człowiek kliknął „Pokaż wszystkie" — klucz `${anchorId}:${section}`.
   *
   * Per sekcja, a nie globalnie: zdjęcie progu w jednym rozwiniętym węźle nie ma
   * powodu wysypywać dwudziestu pozycji przy każdym innym.
   */
  unrestricted?: ReadonlySet<string>;
}

/** Klucz sekcji dla progu zdjętego ręcznie. */
export function sectionKey(anchorId: number, section: RelationDirection): string {
  return `${anchorId}:${section}`;
}

/**
 * Porządek „gdzie się pali" (decyzja 45): najpierw draugry malejąco, potem zadania
 * do zrobienia malejąco, potem alfabetycznie.
 *
 * Używany WYŁĄCZNIE do wyboru, które pozycje przechodzą przez próg — nie do układania
 * ich na ekranie.
 */
function compareByHeat(
  heat: ReadonlyMap<number, { draugrCount: number; activeCount: number }>,
  aId: number,
  aTitle: string,
  bId: number,
  bTitle: string,
): number {
  const a = heat.get(aId);
  const b = heat.get(bId);

  const draugrDiff = (b?.draugrCount ?? 0) - (a?.draugrCount ?? 0);
  if (draugrDiff !== 0) {
    return draugrDiff;
  }

  const activeDiff = (b?.activeCount ?? 0) - (a?.activeCount ?? 0);
  if (activeDiff !== 0) {
    return activeDiff;
  }

  return aTitle.localeCompare(bTitle, 'pl');
}

const SECTION_ORDER = ['has', 'depends-on', 'used-by'] as const;

/**
 * Buduje wiersze do wyświetlenia.
 *
 * Kolejność sekcji jest **stała** (decyzja 51: `has` → `depends-on` → `used-by`,
 * wszystkie na jednym wcięciu) i nie zależy od kolejności w odpowiedzi.
 *
 * Próg zwijania (decyzja 42) **wybiera** pozycje ciepłem (decyzja 45), ale
 * **wyświetla** je alfabetycznie — patrz `BuildOptions.heat`. Bez przekazanego
 * ciepła wybór jest alfabetyczny, czyli zachowanie sprzed decyzji 45.
 */
export function buildRows(data: ConstellationData, options: BuildOptions): TreeRow[] {
  const { expanded, siblingCap, hiddenSections, heat, unrestricted } = options;

  const root = data.nodes.get(data.rootElementId);
  if (!root) {
    return [];
  }

  const rows: TreeRow[] = [];

  function emit(
    elementId: number,
    title: string,
    type: string | null,
    direction: RelationDirection,
    depth: number,
    key: string,
    // Ścieżka od korzenia — jedyny bezpiecznik przed nieskończonym rozwijaniem.
    // `depends-on` ma cykle Z DEFINICJI (spec 3.3), a we własnych danych
    // przykładowych mamy taki cykl: Płatności → Waluta → Konfiguracja → Płatności.
    path: readonly number[],
  ) {
    const repeated = path.includes(elementId);
    const node = data.nodes.get(elementId);

    rows.push({
      kind: 'element',
      key,
      elementId,
      title,
      type,
      depth,
      direction,
      // `null` znaczy „jeszcze nie pytaliśmy", nie „nic tu nie ma".
      expandableCount: node
        ? node.childCount + node.dependsOnCount + node.usedByCount
        : null,
      repeated,
    });

    if (repeated) {
      return;
    }

    const isExpanded = depth === 0 || expanded.has(elementId);
    if (!isExpanded) {
      return;
    }

    const nextPath = [...path, elementId];

    for (const section of SECTION_ORDER) {
      if (hiddenSections?.has(section)) {
        continue;
      }

      const items: readonly (ConstellationNode | ConstellationEdge)[] =
        section === 'has'
          ? (data.children.get(elementId) ?? [])
          : (data.edges.get(elementId) ?? []).filter(
              (edge) => edge.reversed === (section === 'used-by'),
            );

      if (items.length === 0) {
        continue;
      }

      let visible: readonly (ConstellationNode | ConstellationEdge)[] = items;

      const cap = unrestricted?.has(sectionKey(elementId, section)) ? null : siblingCap;

      if (cap !== null && items.length > cap) {
        // Krok 1 — WYBÓR ciepłem (decyzja 45). Bez tego „Walidator kwot", od którego
        // zależy pół modułu, wpada do ukrytej trzynastki tylko dlatego, że jego nazwa
        // zaczyna się na dalszą literę.
        const selected = heat
          ? [...items].sort((a, b) =>
              compareByHeat(heat, a.elementId, a.title, b.elementId, b.title))
          : items;

        // Krok 2 — WYŚWIETLENIE alfabetycznie. Ciepło decyduje, KTO wchodzi, a nie
        // w jakiej kolejności stoi; inaczej wiersze skaczą przy każdym domknięciu
        // cudzego zadania.
        visible = selected
          .slice(0, cap)
          .sort((a, b) => a.title.localeCompare(b.title, 'pl'));
      }

      for (const item of visible) {
        if (section === 'has') {
          const child = item as ConstellationNode;
          emit(
            child.elementId,
            child.title,
            child.type,
            'has',
            depth + 1,
            `${key}/n${child.elementId}`,
            nextPath,
          );
        } else {
          const edge = item as ConstellationEdge;
          emit(
            edge.elementId,
            edge.title,
            edge.type,
            section,
            depth + 1,
            `${key}/e${edge.relationId}`,
            nextPath,
          );
        }
      }

      const hidden = items.length - visible.length;
      if (hidden > 0) {
        rows.push({
          kind: 'more',
          key: `${key}/more-${section}`,
          anchorId: elementId,
          section,
          depth: depth + 1,
          hiddenCount: hidden,
        });
      }
    }
  }

  emit(root.elementId, root.title, root.type, 'has', 0, `n${root.elementId}`, []);

  return rows;
}

/**
 * Czy ten wiersz da się rozwinąć.
 *
 * `expandableCount === null` znaczy „jeszcze nie pytaliśmy" — i wtedy trójkąt
 * MA być, bo ukrycie go byłoby zgadywaniem „pewnie nic tam nie ma". Zależności
 * przychodzą jako adnotacja bez rozwijania (decyzja 70), więc o ich własnych
 * powiązaniach dowiadujemy się dopiero po kliknięciu.
 */
export function isExpandable(row: TreeRow): boolean {
  if (row.kind !== 'element' || row.repeated) {
    return false;
  }

  return row.expandableCount === null || row.expandableCount > 0;
}

/**
 * Ponowne użycie wierszy z poprzedniego układu tam, gdzie nic się nie zmieniło.
 *
 * `buildRows` tworzy nowe obiekty przy każdym przeliczeniu, a wiersz drzewka jest zapamiętany
 * (`memo`) i porównuje `row` po tożsamości — bez tego rozwinięcie jednego węzła przerysowywało
 * wszystkie 1700 wierszy zamiast kilkunastu nowych (2026-08-26). Porównanie płytkie wystarcza:
 * wiersz to same prymitywy.
 */
export function reuseRows(
  previous: ReadonlyMap<string, TreeRow>,
  next: readonly TreeRow[],
): TreeRow[] {
  return next.map((row) => {
    const old = previous.get(row.key);

    return old !== undefined && sameRow(old, row) ? old : row;
  });
}

function sameRow(a: TreeRow, b: TreeRow): boolean {
  const left = a as unknown as Record<string, unknown>;
  const right = b as unknown as Record<string, unknown>;
  const keys = Object.keys(right);

  return (
    keys.length === Object.keys(left).length && keys.every((key) => left[key] === right[key])
  );
}
